import type { AppId } from 'constants/known-apps';
import { getAppsEntities } from 'redux/modules/integrations.selector';
import { getTicApiSlice } from 'redux/modules/tic/api-slice/tic.api.selector';
import { ticApi } from 'redux/modules/tic/api-slice/tic.apiSlice';
import { getContextualTicAccounts } from 'redux/modules/tic/tic.auth.selectors';
import { getAppAccounts, getConnectedAppIds } from 'redux/selectors/apps';
import { createSelector } from 'reselect';

/**
 * Mapping of legacy app IDs to TIC provider IDs for integrations that can have
 * both a legacy and new (TIC) version active simultaneously.
 */
export const DUPLICATE_PROVIDER_MAP: Array<{
  legacyId: AppId;
  ticId: string;
  displayName: string;
}> = [
  { legacyId: 'clickup', ticId: 'clickup', displayName: 'ClickUp' },
  { legacyId: 'asana_sync', ticId: 'asana', displayName: 'Asana' },
  { legacyId: 'trello', ticId: 'trello', displayName: 'Trello' },
  { legacyId: 'monday', ticId: 'monday', displayName: 'Monday' },
];

export type DuplicateIntegrationProvider = {
  legacyId: AppId;
  ticId: string;
  displayName: string;
  /** Numeric app ID needed for `removeAppAccount`. Undefined if app entity not found. */
  numericAppId?: number;
  /** Numeric IDs of connected accounts for this legacy app. */
  accountIds: number[];
};

/**
 * Returns a map from TIC provider ID to its legacy counterpart info,
 * for all legacy integrations currently connected.
 * Used to show warnings in the setup wizard when connecting a new TIC integration.
 */
export const getConnectedLegacyCounterparts = createSelector(
  [getConnectedAppIds, getAppsEntities, getAppAccounts],
  (connectedAppIds, appsEntities, appAccounts): Map<string, DuplicateIntegrationProvider> => {
    const map = new Map<string, DuplicateIntegrationProvider>();
    for (const entry of DUPLICATE_PROVIDER_MAP) {
      if (connectedAppIds.includes(entry.legacyId)) {
        const app = Object.values(appsEntities).find(a => a.id === entry.legacyId);
        const accounts = app ? (appAccounts[app.app_id] ?? []) : [];
        map.set(entry.ticId, {
          ...entry,
          numericAppId: app?.app_id,
          accountIds: accounts.map(a => a.id),
        });
      }
    }
    return map;
  }
);

/**
 * Returns providers where both legacy and TIC integrations are connected.
 * Empty array if no duplicates or if TIC data isn't loaded yet.
 */
export const getDuplicateIntegrationProviders = createSelector(
  [getTicApiSlice, getContextualTicAccounts, getConnectedAppIds],
  (apiState, { workspace, personalSpace }, connectedAppIds): DuplicateIntegrationProvider[] => {
    const accountId = workspace?.id ?? personalSpace?.id;
    if (!accountId) return [];

    const integrationsResult = ticApi.endpoints.getIntegrations.select({ accountId })({
      [ticApi.reducerPath]: apiState,
    });

    const integrations = integrationsResult.data?.data || [];

    return DUPLICATE_PROVIDER_MAP.map(entry => ({ ...entry, accountIds: [] as number[] })).filter(
      ({ legacyId, ticId }) => {
        const legacyConnected = connectedAppIds.includes(legacyId);
        if (!legacyConnected) return false;

        const ticIntegration = integrations.find(i => i.id === ticId);
        if (!ticIntegration) return false;

        const ticConnected = ticIntegration.account?.connection?.connected === true;

        return ticConnected;
      }
    );
  }
);
